import { useCallback, useEffect, useMemo, useState } from "react";

import { api, ApiError } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { Skeleton } from "@/components/Skeleton";

// ---------------------------------------------------------------------------
// types — mirror the backend pydantic schemas in app/schemas/street_mapping.py
// ---------------------------------------------------------------------------

type MapStatus = "all" | "mapped" | "unmapped";
type Level = "districts" | "urbans" | "streets";

interface MapMeta {
  confidence: string | null;
  source: string | null;
  notes: string | null;
}

interface SsRef {
  id: number;
  title: string | null;
}
interface SsSubRef extends SsRef {
  district_id: number;
  district_title: string | null;
}
interface SsStreetRef extends SsSubRef {
  subdistrict_id: number;
  subdistrict_title: string | null;
}

interface DistrictItem {
  myhome_id: number;
  slug: string | null;
  display_name_ka: string | null;
  display_name_en: string | null;
  ss: SsRef | null;
  map: MapMeta | null;
}

interface UrbanItem extends Omit<DistrictItem, "ss"> {
  myhome_district_id: number;
  myhome_district_name_ka: string | null;
  ss: SsSubRef | null;
}

interface PageOut<T> {
  total: number;
  mapped_count: number;
  unmapped_count: number;
  page: number;
  page_size: number;
  items: T[];
}

interface SummaryLevel {
  total: number;
  mapped: number;
  unmapped: number;
}
interface Summary {
  districts: SummaryLevel;
  urbans: SummaryLevel;
  streets: SummaryLevel;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

function fmtConfidence(c: string | null): string {
  if (!c) return "—";
  const n = Number(c);
  if (Number.isNaN(n)) return c;
  return n.toFixed(2);
}

// ---------------------------------------------------------------------------
// page shell
// ---------------------------------------------------------------------------

export function StreetMapping() {
  const [tab, setTab] = useState<Level>("districts");
  const [summary, setSummary] = useState<Summary | null>(null);
  const [summaryErr, setSummaryErr] = useState<string | null>(null);

  const refreshSummary = useCallback(async () => {
    try {
      const s = await api<Summary>("/api/street-mapping/summary");
      setSummary(s);
    } catch (e) {
      setSummaryErr(e instanceof ApiError ? e.message : "Failed to load summary");
    }
  }, []);

  useEffect(() => {
    refreshSummary();
  }, [refreshSummary]);

  return (
    <div className="w-full space-y-5">
      <PageHeader
        title="Street mapping"
        subtitle={
          <span>
            Review and fix the cross-platform mapping between MyHome and SS at the
            district, urban / sub-district, and street levels. Heuristic seeds were
            applied automatically; gaps below need a human eye.
          </span>
        }
      />

      {summaryErr && (
        <div className="card p-3 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {summaryErr}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SummaryCard
          label="Districts"
          tone={tab === "districts" ? "active" : "default"}
          onClick={() => setTab("districts")}
          summary={summary?.districts}
        />
        <SummaryCard
          label="Urbans / Sub-districts"
          tone={tab === "urbans" ? "active" : "default"}
          onClick={() => setTab("urbans")}
          summary={summary?.urbans}
        />
        <SummaryCard
          label="Streets"
          tone={tab === "streets" ? "active" : "default"}
          onClick={() => setTab("streets")}
          summary={summary?.streets}
        />
      </div>

      <MappingTab level={tab} onChange={refreshSummary} />
    </div>
  );
}

function SummaryCard({
  label,
  summary,
  tone,
  onClick,
}: {
  label: string;
  summary?: SummaryLevel;
  tone: "active" | "default";
  onClick: () => void;
}) {
  const pct = summary && summary.total > 0
    ? Math.round((summary.mapped / summary.total) * 100)
    : null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`card p-4 text-left transition w-full ${
        tone === "active"
          ? "border-sky-400/40 bg-sky-500/[0.06]"
          : "hover:border-white/15"
      }`}
    >
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      {summary ? (
        <>
          <div className="mt-1 text-lg font-semibold text-slate-100">
            {summary.mapped} / {summary.total}
            <span className="text-slate-500 text-xs font-normal ml-2">
              {pct !== null ? `${pct}% mapped` : ""}
            </span>
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            {summary.unmapped} left unmapped
          </div>
        </>
      ) : (
        <Skeleton className="h-10 mt-1" />
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// tab body
// ---------------------------------------------------------------------------

function MappingTab({
  level,
  onChange,
}: {
  level: Level;
  onChange: () => void;
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<MapStatus>("unmapped");
  const [districtFilter, setDistrictFilter] = useState<number | null>(null);
  const [urbanFilter, setUrbanFilter] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const [items, setItems] = useState<unknown[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Picker state — which row is currently being edited
  const [pickerFor, setPickerFor] = useState<unknown | null>(null);

  // Reset filters when switching levels.
  useEffect(() => {
    setSearch("");
    setStatusFilter("unmapped");
    setDistrictFilter(null);
    setUrbanFilter(null);
    setPage(1);
  }, [level]);

  // Districts list — used to populate the district filter on urbans/streets.
  const [districtOptions, setDistrictOptions] = useState<
    { id: number; label: string }[]
  >([]);
  useEffect(() => {
    (async () => {
      try {
        const r = await api<PageOut<DistrictItem>>(
          "/api/street-mapping/districts?page_size=100",
        );
        setDistrictOptions(
          r.items.map((d) => ({
            id: d.myhome_id,
            label: d.display_name_ka || d.slug || String(d.myhome_id),
          })),
        );
      } catch {
        /* non-critical */
      }
    })();
  }, []);

  // Urbans list (filtered by selected district) for the streets tab filter.
  const [urbanOptions, setUrbanOptions] = useState<
    { id: number; label: string }[]
  >([]);
  useEffect(() => {
    if (level !== "streets") {
      setUrbanOptions([]);
      return;
    }
    if (districtFilter == null) {
      setUrbanOptions([]);
      return;
    }
    (async () => {
      try {
        const r = await api<PageOut<UrbanItem>>(
          `/api/street-mapping/urbans?district_id=${districtFilter}&page_size=200`,
        );
        setUrbanOptions(
          r.items.map((u) => ({
            id: u.myhome_id,
            label: u.display_name_ka || u.slug || String(u.myhome_id),
          })),
        );
      } catch {
        /* non-critical */
      }
    })();
  }, [level, districtFilter]);

  const queryString = useMemo(() => {
    const qs = new URLSearchParams();
    if (search.trim()) qs.set("q", search.trim());
    if (statusFilter !== "all") qs.set("mapped", statusFilter);
    if (districtFilter != null && level !== "districts")
      qs.set("district_id", String(districtFilter));
    if (urbanFilter != null && level === "streets")
      qs.set("urban_id", String(urbanFilter));
    qs.set("page", String(page));
    qs.set("page_size", String(pageSize));
    return qs.toString();
  }, [search, statusFilter, districtFilter, urbanFilter, page, level]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await api<PageOut<unknown>>(
        `/api/street-mapping/${level}?${queryString}`,
      );
      setItems(r.items);
      setTotal(r.total);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [level, queryString]);

  // Debounce the search box (300 ms) so each keystroke isn't a request.
  useEffect(() => {
    const t = setTimeout(refresh, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [refresh, search]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
        <div className="flex-1 min-w-0">
          <label className="text-xs text-slate-500">Search (Georgian/English/slug)</label>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="e.g. ვაკე, გლდანი, abashvili…"
            className="mt-1 w-full px-3 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-sm text-slate-100"
          />
        </div>
        <div>
          <label className="text-xs text-slate-500">Show</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as MapStatus);
              setPage(1);
            }}
            className="mt-1 w-full sm:w-auto px-3 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-sm text-slate-100"
          >
            <option value="all">All</option>
            <option value="unmapped">Unmapped</option>
            <option value="mapped">Mapped</option>
          </select>
        </div>
        {level !== "districts" && (
          <div>
            <label className="text-xs text-slate-500">MyHome district</label>
            <select
              value={districtFilter ?? ""}
              onChange={(e) => {
                setDistrictFilter(
                  e.target.value === "" ? null : Number(e.target.value),
                );
                setUrbanFilter(null);
                setPage(1);
              }}
              className="mt-1 w-full sm:w-auto px-3 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-sm text-slate-100"
            >
              <option value="">Any</option>
              {districtOptions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        )}
        {level === "streets" && (
          <div>
            <label className="text-xs text-slate-500">MyHome urban</label>
            <select
              value={urbanFilter ?? ""}
              onChange={(e) => {
                setUrbanFilter(
                  e.target.value === "" ? null : Number(e.target.value),
                );
                setPage(1);
              }}
              disabled={districtFilter == null}
              className="mt-1 w-full sm:w-auto px-3 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-sm text-slate-100 disabled:opacity-50"
            >
              <option value="">Any</option>
              {urbanOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {err && (
        <div className="card p-3 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
          {err}
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-400">
              <tr className="border-b border-white/5">
                <th className="px-3 py-2 font-normal w-1/3">MyHome row</th>
                <th className="px-3 py-2 font-normal w-1/3">SS counterpart</th>
                <th className="px-3 py-2 font-normal w-24">Conf / src</th>
                <th className="px-3 py-2 font-normal text-right w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6">
                    <Skeleton className="h-32" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-slate-500">
                    Nothing matches the current filters.
                  </td>
                </tr>
              ) : (
                items.map((item: any) => (
                  <Row
                    key={item.myhome_id}
                    level={level}
                    item={item}
                    onEdit={() => setPickerFor(item)}
                    onClear={async () => {
                      try {
                        await api(
                          `/api/street-mapping/${level}/${item.myhome_id}`,
                          { method: "DELETE" },
                        );
                        await refresh();
                        onChange();
                      } catch (e) {
                        setErr(e instanceof ApiError ? e.message : "Clear failed");
                      }
                    }}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pager */}
      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            Page {page} of {totalPages} · {total} rows
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 rounded border border-white/10 disabled:opacity-30"
            >
              ← Prev
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="px-3 py-1 rounded border border-white/10 disabled:opacity-30"
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {pickerFor !== null && (
        <Picker
          level={level}
          item={pickerFor as any}
          onClose={() => setPickerFor(null)}
          onSaved={async () => {
            setPickerFor(null);
            await refresh();
            onChange();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// row
// ---------------------------------------------------------------------------

function Row({
  level,
  item,
  onEdit,
  onClear,
}: {
  level: Level;
  item: any;
  onEdit: () => void;
  onClear: () => void;
}) {
  const myhomeLabel =
    level === "streets"
      ? item.name_ka || item.street_key || `#${item.myhome_id}`
      : item.display_name_ka || item.slug || `#${item.myhome_id}`;

  const myhomeSub =
    level === "districts"
      ? item.display_name_en || ""
      : level === "urbans"
        ? `under ${item.myhome_district_name_ka || "?"}`
        : `${item.myhome_urban_name_ka || "?"} · ${item.myhome_district_name_ka || "?"}`;

  const ssLabel = item.ss
    ? level === "streets"
      ? item.ss.title
      : item.ss.title
    : null;

  const ssSub = item.ss
    ? level === "districts"
      ? ""
      : level === "urbans"
        ? `under ${(item.ss as SsSubRef).district_title || "?"}`
        : `${(item.ss as SsStreetRef).subdistrict_title || "?"} · ${(item.ss as SsStreetRef).district_title || "?"}`
    : "";

  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.02]">
      <td className="px-3 py-2">
        <div className="text-slate-100">{myhomeLabel}</div>
        <div className="text-xs text-slate-500">
          #{item.myhome_id} · {myhomeSub}
        </div>
      </td>
      <td className="px-3 py-2">
        {ssLabel ? (
          <>
            <div className="text-slate-100">{ssLabel}</div>
            <div className="text-xs text-slate-500">
              #{item.ss.id} · {ssSub}
            </div>
          </>
        ) : (
          <span className="text-slate-500 italic">— unmapped —</span>
        )}
      </td>
      <td className="px-3 py-2 text-xs text-slate-400">
        {item.map ? (
          <>
            <div>{fmtConfidence(item.map.confidence)}</div>
            <div className="text-slate-500">{item.map.source}</div>
          </>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        <button
          type="button"
          onClick={onEdit}
          className="px-2 py-1 text-xs rounded border border-white/10 hover:bg-white/[0.04]"
        >
          {item.ss ? "Re-map" : "Map"}
        </button>
        {item.ss && (
          <button
            type="button"
            onClick={onClear}
            className="ml-1 px-2 py-1 text-xs rounded border border-rose-500/20 text-rose-300 hover:bg-rose-500/[0.06]"
          >
            Clear
          </button>
        )}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// picker modal
// ---------------------------------------------------------------------------

interface CandidateRowOut<T> {
  items: T[];
  out_of_scope?: boolean;
}

function Picker({
  level,
  item,
  onClose,
  onSaved,
}: {
  level: Level;
  item: any;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [q, setQ] = useState("");
  const [broaden, setBroaden] = useState(false);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [outOfScope, setOutOfScope] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState<number | null>(null);

  // Resolve myhome parent ids -> SS parent ids for scoping the candidate
  // search. We do one round-trip per row open; small.
  const [scopeIds, setScopeIds] = useState<{
    ssDistrictId: number | null;
    ssSubdistrictId: number | null;
  }>({ ssDistrictId: null, ssSubdistrictId: null });

  useEffect(() => {
    (async () => {
      if (level === "districts") return;
      // Find SS district id from district_map for this myhome district.
      try {
        let myhomeDid: number | null = null;
        let myhomeUid: number | null = null;
        if (level === "urbans") {
          myhomeDid = item.myhome_district_id;
        } else if (level === "streets") {
          myhomeDid = item.myhome_district_id;
          myhomeUid = item.myhome_urban_id;
        }
        let ssDistrictId: number | null = null;
        let ssSubId: number | null = null;
        if (myhomeDid != null) {
          const r = await api<PageOut<DistrictItem>>(
            `/api/street-mapping/districts?mapped=mapped&page_size=10`,
          );
          const hit = r.items.find((d) => d.myhome_id === myhomeDid);
          if (hit && hit.ss) ssDistrictId = hit.ss.id;
        }
        if (myhomeUid != null) {
          const r = await api<PageOut<UrbanItem>>(
            `/api/street-mapping/urbans?mapped=mapped&page_size=200`,
          );
          const hit = r.items.find((u) => u.myhome_id === myhomeUid);
          if (hit && hit.ss) ssSubId = hit.ss.id;
        }
        setScopeIds({ ssDistrictId, ssSubdistrictId: ssSubId });
      } catch {
        /* non-critical */
      }
    })();
  }, [level, item]);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (broaden) params.set("broaden", "true");
      if (level === "urbans" && scopeIds.ssDistrictId != null) {
        params.set("parent_district_id", String(scopeIds.ssDistrictId));
      }
      if (level === "streets") {
        if (scopeIds.ssSubdistrictId != null)
          params.set("parent_subdistrict_id", String(scopeIds.ssSubdistrictId));
        else if (scopeIds.ssDistrictId != null)
          params.set("parent_district_id", String(scopeIds.ssDistrictId));
      }
      const r = await api<CandidateRowOut<any>>(
        `/api/street-mapping/${level}/candidates?${params.toString()}`,
      );
      setCandidates(r.items);
      setOutOfScope(!!r.out_of_scope);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }, [q, broaden, level, scopeIds]);

  useEffect(() => {
    const t = setTimeout(fetchCandidates, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchCandidates, q]);

  async function pick(ssId: number) {
    setSaving(ssId);
    setErr(null);
    try {
      await api(`/api/street-mapping/${level}/${item.myhome_id}`, {
        method: "PUT",
        json: { ss_id: ssId, notes: notes.trim() || null },
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Save failed");
    } finally {
      setSaving(null);
    }
  }

  const myhomeLabel =
    level === "streets"
      ? item.name_ka || item.street_key
      : item.display_name_ka || item.slug;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-white/5 flex items-start justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase">
              Map {level === "districts" ? "district" : level === "urbans" ? "urban" : "street"}
            </div>
            <div className="text-slate-100 font-medium mt-0.5">{myhomeLabel}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              MyHome #{item.myhome_id}
              {level !== "districts" && (
                <>
                  {" · "}
                  {item.myhome_district_name_ka || "?"}
                  {level === "streets" && item.myhome_urban_name_ka
                    ? ` / ${item.myhome_urban_name_ka}`
                    : ""}
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-200 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="px-4 py-3 border-b border-white/5 space-y-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search SS counterpart…"
            className="w-full px-3 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-sm text-slate-100"
            autoFocus
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-slate-500">
            <div className="flex flex-col gap-1">
              {level !== "districts" && scopeIds.ssDistrictId != null && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={broaden}
                    onChange={(e) => setBroaden(e.target.checked)}
                  />
                  Search across all SS (ignore mapped parent district)
                </label>
              )}
              <div className="text-slate-600 italic">
                Mapping is many-to-one — picking an SS row that's already used by
                another myhome row is fine.
              </div>
            </div>
            <span>
              {(level === "urbans" || level === "streets") && scopeIds.ssDistrictId == null
                ? "No mapped parent district — searching all of SS."
                : ""}
            </span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {err && (
            <div className="m-3 card p-3 border-rose-500/25 bg-rose-500/[0.04] text-rose-300 text-sm">
              {err}
            </div>
          )}
          {outOfScope && !broaden && (
            <div className="m-3 px-3 py-2 rounded-md border border-amber-500/25 bg-amber-500/[0.04] text-amber-200 text-xs">
              No matches in mapped parent district — showing city-wide candidates.
              The two platforms sometimes classify the same place under different
              parent districts.
            </div>
          )}
          {loading ? (
            <div className="p-4">
              <Skeleton className="h-32" />
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-6 text-center text-slate-500 text-sm">
              No SS candidates match.
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {candidates.map((c) => {
                const sub =
                  level === "urbans"
                    ? `under ${c.district_title || "?"}`
                    : level === "streets"
                      ? `${c.subdistrict_title || "?"} · ${c.district_title || "?"}`
                      : "";
                return (
                  <li
                    key={c.id}
                    className="px-4 py-2 flex items-center justify-between hover:bg-white/[0.02]"
                  >
                    <div className="min-w-0">
                      <div className="text-slate-100 truncate flex items-center gap-2">
                        <span>{c.title}</span>
                        {typeof c.mapped_count === "number" && c.mapped_count > 0 && (
                          <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">
                            used by {c.mapped_count}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">
                        #{c.id} {sub && `· ${sub}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => pick(c.id)}
                      disabled={saving === c.id}
                      className="ml-3 px-2 py-1 text-xs rounded border border-sky-500/30 text-sky-200 hover:bg-sky-500/[0.08] disabled:opacity-50"
                    >
                      {saving === c.id ? "Saving…" : "Pick"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="px-4 py-3 border-t border-white/5">
          <label className="text-xs text-slate-500">Notes (optional)</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="why this mapping (audit trail)"
            className="mt-1 w-full px-3 py-2 bg-slate-900/50 border border-white/10 rounded-lg text-sm text-slate-100"
          />
        </div>
      </div>
    </div>
  );
}
